/**
 * MinimalHeader Component
 *
 * Minimal header for immersive pages (Quiz Play, Map of Knowledge full-screen).
 * Based on UX_DESIGN.md Pattern 4: Immersive (lines 436-458).
 *
 * Features:
 * - Logo (links to home)
 * - Title/subtitle (optional)
 * - Exit button
 * - Progress bar (optional)
 */

import { Link } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { WifiOff } from "lucide-react";
import { isOnline, setupOnlineOfflineListeners } from "@/utils/networkStatus";

interface MinimalHeaderProps {
  /**
   * Optional title to show
   */
  title?: string;
  /**
   * Optional subtitle
   */
  subtitle?: string;
  /**
   * Action buttons (e.g., Exit button)
   */
  actions?: ReactNode;
  /**
   * Show progress bar at top
   */
  showProgress?: boolean;
  /**
   * Progress percentage (0-100)
   */
  progress?: number;
}

export function MinimalHeader({
  title,
  subtitle,
  actions,
  showProgress = false,
  progress = 0,
}: MinimalHeaderProps) {
  // Online/offline state
  const [online, setOnline] = useState(() => isOnline());

  useEffect(() => {
    const cleanup = setupOnlineOfflineListeners(
      () => setOnline(true),
      () => setOnline(false)
    );
    return cleanup;
  }, []);

  return (
    <header className="sticky top-0 z-20 bg-card border-b">
      {/* Progress bar at very top */}
      {showProgress && (
        <div
          className="h-1 bg-primary transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      )}

      <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
        {/* Left: Logo + Offline Indicator */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <Link
            to="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img
              src="/logo.svg"
              alt="MapQuizzr Logo"
              className="w-8 h-8 drop-shadow-sm"
            />
            <span className="font-bold text-lg hidden sm:inline">MapQuizzr</span>
          </Link>

          {/* Offline Indicator */}
          {!online && (
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 flex items-center gap-1">
              <WifiOff className="w-3 h-3" />
              <span className="hidden sm:inline">Offline</span>
            </Badge>
          )}
        </div>

        {/* Center: Title (if provided) */}
        {title && (
          <div className="flex-1 min-w-0 text-center">
            <h1 className="text-lg font-bold truncate">{title}</h1>
            {subtitle && (
              <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
            )}
          </div>
        )}

        {/* Right: Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {actions}
        </div>
      </div>
    </header>
  );
}
